import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../errors.js';
import { findNewAuditId, type AdminHttpClient } from '../http.js';
import type { AdminGroupView, ServiceUser, ToastMessage } from '../types.js';
import { FormField } from '../components/FormField.js';
import { DataTable } from '../components/DataTable.js';
import { TabStrip } from '../components/TabStrip.js';
import { useTabState } from '../hooks/index.js';
import { downloadCsv, parseCsv } from '../utils/csv.js';
import { computeGroupMembershipDiff, type GroupImportFailure, type GroupImportRow, type GroupMembershipOperation } from '../utils/group-membership.js';

const MEMBER_PAGE_SIZE = 20;
const CANDIDATE_LIMIT = 10;

// The bulk-CSV panel (file picker + textarea + preview table) is as tall as the
// group table and member drilldown combined, and pushed them off-screen while
// sitting idle. It is a separate task, so it gets a separate tab.
const TABS = [
  { id: 'groups', label: 'groups.tabGroups' },
  { id: 'csv', label: 'groups.tabCsv' },
] as const;
type TabId = (typeof TABS)[number]['id'];
const TAB_IDS = TABS.map((item) => item.id);

export function GroupsScreen({ http, onToast }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useTabState<TabId>('veluga.admin.groupsTab', 'groups', TAB_IDS);
  const [groups, setGroups] = useState<AdminGroupView[]>([]);
  const [members, setMembers] = useState<ServiceUser[]>([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [memberPage, setMemberPage] = useState(0);
  const [memberQuery, setMemberQuery] = useState('');
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidateUsers, setCandidateUsers] = useState<ServiceUser[]>([]);
  const [name, setName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [importText, setImportText] = useState('');
  const [importMode, setImportMode] = useState<'add-only' | 'replace'>('add-only');
  const [importOperations, setImportOperations] = useState<GroupMembershipOperation[]>([]);
  const [importInvalid, setImportInvalid] = useState<GroupImportFailure[]>([]);
  const [importEmails, setImportEmails] = useState<Map<string, string>>(new Map());
  const [importFailures, setImportFailures] = useState<Array<{ operation: GroupMembershipOperation; message: string }>>([]);
  const [importApplied, setImportApplied] = useState(0);
  const [importBusy, setImportBusy] = useState(false);
  const importPreviewRevision = useRef(0);

  const memberRequest = useRef(0);
  const candidateRequest = useRef(0);
  const selectedGroupRef = useRef(selectedGroupId);
  selectedGroupRef.current = selectedGroupId;
  const detailHeading = useRef<HTMLHeadingElement>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const selectedGroup = groups.find((group) => group.id === selectedGroupId);
  // A member belongs to at most one group, so the loaded member lists already
  // say which. Naming it on the candidate makes the rule visible before the
  // server has to refuse the add.
  const groupNameByMember = new Map(
    groups.flatMap((group) => group.memberIds.map((userId) => [userId, group.name] as const))
  );
  const blockingGroupFor = (userId: string): string | undefined => {
    const current = groupNameByMember.get(userId);
    return current === undefined || current === selectedGroup?.name ? undefined : current;
  };

  const loadGroups = useCallback(async () => {
    const nextGroups = (await http.listGroups()) as AdminGroupView[];
    setGroups(nextGroups as AdminGroupView[]);
    setSelectedGroupId((current) => nextGroups.some((group) => group.id === current) ? current : nextGroups[0]?.id ?? '');
  }, [http]);

  const loadMembers = useCallback(
    async (groupId: string, page: number, q: string) => {
      if (groupId !== selectedGroupRef.current) return;
      const request = ++memberRequest.current;
      setMembers([]);
      setMemberTotal(0);
      setMemberLoading(Boolean(groupId));
      if (!groupId) return;
      try {
        let nextPage = page;
        let result = await http.listUsers({ groupId, limit: MEMBER_PAGE_SIZE,
          offset: nextPage * MEMBER_PAGE_SIZE, ...(q.trim() ? { q: q.trim() } : {}) });
        while (result.items.length === 0 && nextPage > 0) {
          if (request !== memberRequest.current || groupId !== selectedGroupRef.current) return;
          nextPage = Math.min(nextPage - 1, Math.max(0, Math.ceil(result.total / MEMBER_PAGE_SIZE) - 1));
          result = await http.listUsers({ groupId, limit: MEMBER_PAGE_SIZE,
            offset: nextPage * MEMBER_PAGE_SIZE, ...(q.trim() ? { q: q.trim() } : {}) });
        }
        if (request !== memberRequest.current || groupId !== selectedGroupRef.current) return;
        setMembers(result.items);
        setMemberTotal(result.total);
        setMemberPage(nextPage);
      } catch (error) {
        if (request === memberRequest.current && groupId === selectedGroupRef.current)
          onToast({ tone: 'error', message: describeError(error, t) });
      } finally {
        if (request === memberRequest.current && groupId === selectedGroupRef.current) setMemberLoading(false);
      }
    },
    [http, onToast, t]
  );

  const loadCandidates = useCallback(
    async (q: string) => {
      const request = ++candidateRequest.current;
      const result = await http.listUsers({
        limit: CANDIDATE_LIMIT,
        ...(q.trim() ? { q: q.trim() } : {})
      });
      if (request !== candidateRequest.current) return;
      setCandidateUsers(result.items);
      setSelectedUserId((current) => result.items.some((user) => user.id === current) ? current : '');
    },
    [http]
  );

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    void loadMembers(selectedGroupId, memberPage, memberQuery);
  }, [loadMembers, memberPage, memberQuery, selectedGroupId]);

  useEffect(() => {
    void loadCandidates(candidateQuery).catch((error) => onToast({ tone: 'error', message: describeError(error, t) }));
  }, [candidateQuery, loadCandidates, onToast, t]);

  async function create(event: FormEvent) {
    event.preventDefault();
    try {
      const before = await http.listAudit();
      await http.createGroup(name.trim());
      setName('');
      await loadGroups();
      const auditId = findNewAuditId(before, await http.listAudit(), { eventType: 'admin.group.updated' });
      onToast({ tone: 'success', message: t('common.changedWithAudit', { auditId: auditId ?? t('common.pending') }) });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function updateMember(action: 'add' | 'remove', userId = selectedUserId) {
    if (!selectedGroupId || !userId) return;
    try {
      const before = await http.listAudit();
      await http.updateGroupMembers(
        selectedGroupId,
        action === 'add' ? { addUserId: userId } : { removeUserId: userId }
      );
      await Promise.all([
        loadGroups(),
        loadMembers(selectedGroupId, memberPage, memberQuery),
        loadCandidates(candidateQuery)
      ]);
      const auditId = findNewAuditId(before, await http.listAudit(), {
        eventType: 'admin.group.updated',
        targetId: selectedGroupId
      });
      onToast({ tone: 'success', message: t('common.changedWithAudit', { auditId: auditId ?? t('common.pending') }) });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function allUsers(groupId?: string): Promise<ServiceUser[]> {
    const users: ServiceUser[] = [];
    for (let offset = 0;; offset += 200) {
      const page = await http.listUsers({ ...(groupId ? { groupId } : {}), limit: 200, offset });
      users.push(...page.items);
      // Stop on an empty page too — guards against an inconsistent total that
      // would otherwise spin this loop forever.
      if (page.items.length === 0 || users.length >= page.total) return users;
    }
  }

  function clearImportPreview() {
    importPreviewRevision.current += 1;
    setImportOperations([]);
    setImportInvalid([]);
    setImportEmails(new Map());
    setImportFailures([]);
    setImportApplied(0);
  }

  async function previewImport() {
    clearImportPreview();
    const revision = importPreviewRevision.current;
    try {
      const parsed = parseCsv(importText);
      const hasHeader = parsed[0]?.[0]?.trim().toLowerCase() === 'email' && parsed[0]?.[2]?.trim().toLowerCase() === 'group';
      const rows: GroupImportRow[] = parsed.slice(hasHeader ? 1 : 0).map(([email = '', , group = ''], index) => ({ email, group, line: index + (hasHeader ? 2 : 1) })).filter((row) => row.email || row.group);
      const users = await allUsers();
      if (revision !== importPreviewRevision.current) return;
      const result = computeGroupMembershipDiff(
        new Map(groups.map((group) => [group.id, new Set(group.memberIds)])),
        new Map(users.map((user) => [user.email.toLowerCase(), user.id])),
        new Map(groups.map((group) => [group.name, group.id])),
        rows,
        importMode
      );
      setImportOperations(result.operations);
      setImportInvalid(result.invalid);
      setImportEmails(new Map(users.map((user) => [user.id, user.email])));
    } catch (error) {
      if (revision === importPreviewRevision.current) onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function exportGroups() {
    try {
      const users = await allUsers();
      const memberships = new Map<string, string[]>();
      for (const group of groups) for (const userId of group.memberIds) memberships.set(userId, [...(memberships.get(userId) ?? []), group.name]);
      downloadCsv('veluga-users-groups.csv', [['email', 'name', 'group'], ...users.flatMap((user) => (memberships.get(user.id) ?? ['']).map((group) => [user.email, user.name, group]))]);
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function applyImport(operations = importOperations) {
    setImportBusy(true);
    setImportApplied(0);
    const failures: Array<{ operation: GroupMembershipOperation; message: string }> = [];
    let applied = 0;
    for (const operation of operations) {
      try {
        await http.updateGroupMembers(operation.groupId, operation.action === 'add' ? { addUserId: operation.userId } : { removeUserId: operation.userId });
        applied += 1;
        setImportApplied((current) => current + 1);
      } catch (error) {
        failures.push({ operation, message: describeError(error, t) });
      }
    }
    try {
      await Promise.all([loadGroups(), loadMembers(selectedGroupId, memberPage, memberQuery)]);
      setImportFailures(failures);
      setImportOperations(failures.map(({ operation }) => operation));
      if (applied > 0) onToast({ tone: 'success', message: t('common.changed') });
      if (failures.length > 0) onToast({ tone: 'error', message: t('groups.importFailed', { count: failures.length }) });
    } catch (error) { onToast({ tone: 'error', message: describeError(error, t) }); }
    finally { setImportBusy(false); }
  }

  return (
    <section className="content-stack">
      <p className="screen-help">{t('screenHelp.groups')}</p>
      <TabStrip
        tabs={TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
        value={tab}
        onChange={setTab}
        ariaLabel={t('nav.Groups')}
        level="nested"
      />
      {/* Both tabs stay mounted (hidden, not unmounted) so a pasted CSV and its
          preview survive a trip to the group table — and the table reflects an
          applied import immediately, since `groups` is shared parent state.
          The wrappers carry no layout class on purpose: an author `display`
          rule beats the UA `[hidden]` rule (see .tab-strip[hidden]). */}
      <div hidden={tab !== 'csv'}>
      <section className="panel content-stack">
        <div className="row-actions"><h2>{t('groups.csvTitle')}</h2><button type="button" className="secondary" onClick={() => void exportGroups()}>{t('groups.exportCsv')}</button></div>
        <label>{t('users.csvFile')}<input type="file" accept=".csv,text/csv" disabled={importBusy} onChange={(event) => { const file = event.target.files?.[0]; if (file) void file.text().then((text) => { setImportText(text); clearImportPreview(); }); }} /></label>
        <label>{t('users.csvText')}<textarea value={importText} onChange={(event) => { setImportText(event.target.value); clearImportPreview(); }} placeholder="email,name,group" disabled={importBusy} rows={5} /></label>
        <label><span id="group-import-mode">{t('groups.importMode')}</span><select aria-labelledby="group-import-mode" value={importMode} disabled={importBusy} onChange={(event) => { setImportMode(event.target.value as 'add-only' | 'replace'); clearImportPreview(); }}><option value="add-only">{t('groups.addOnly')}</option><option value="replace">{t('groups.replace')}</option></select></label>
        <div className="row-actions"><button type="button" disabled={importBusy || !importText.trim()} onClick={() => void previewImport()}>{t('groups.preview')}</button><button type="button" disabled={importBusy || importOperations.length === 0} onClick={() => void applyImport()}>{t('groups.applyImport', { count: importOperations.length })}</button>{importFailures.length > 0 && <button type="button" disabled={importBusy} onClick={() => void applyImport(importFailures.map(({ operation }) => operation))}>{t('groups.retryFailed', { count: importFailures.length })}</button>}</div>
        <div className="muted">{t('groups.importSummary', { changes: importOperations.length, invalid: importInvalid.length, applied: importApplied })}</div>
        {(importOperations.length > 0 || importInvalid.length > 0 || importFailures.length > 0) && <div className="table-wrap"><table><thead><tr><th>{t('groups.group')}</th><th>{t('users.email')}</th><th>{t('groups.action')}</th><th>{t('groups.status')}</th></tr></thead><tbody>{importOperations.filter((operation) => !importFailures.some((failure) => failure.operation.groupId === operation.groupId && failure.operation.userId === operation.userId && failure.operation.action === operation.action)).map((operation) => <tr key={`${operation.groupId}-${operation.userId}-${operation.action}`}><td>{groups.find((group) => group.id === operation.groupId)?.name}</td><td>{importEmails.get(operation.userId) ?? operation.userId}</td><td>{t(`groups.${operation.action}`)}</td><td>{t('groups.ready')}</td></tr>)}{importInvalid.map(({ row, reason }) => <tr key={`invalid-${row.line}`}><td>{row.group}</td><td>{row.email}</td><td>{t('groups.noChange')}</td><td className="warning">{t(`groups.${reason}`)}</td></tr>)}{importFailures.map(({ operation, message }) => <tr key={`failure-${operation.groupId}-${operation.userId}-${operation.action}`}><td>{groups.find((group) => group.id === operation.groupId)?.name}</td><td>{importEmails.get(operation.userId) ?? operation.userId}</td><td>{t(`groups.${operation.action}`)}</td><td className="warning">{message}</td></tr>)}</tbody></table></div>}
      </section>
      </div>

      <div hidden={tab !== 'groups'}>
      <div className="content-stack">
      <form className="toolbar-form group-create-form" onSubmit={create}>
        <FormField label={t('groups.newGroupName')} placeholder={t('groups.newGroupName')} value={name} onChange={(event) => setName(event.target.value)} />
        <button disabled={!name.trim()}>{t('groups.createGroup')}</button>
      </form>
      <div className="groups-workspace">
      <section className="panel group-list">
        <h2>{t('groups.groups')}</h2>
        <DataTable
          rows={groups}
          empty={t('groups.noGroups')}
          mobileCards
          searchText={(group) => `${group.name} ${group.id}`}
          searchPlaceholder={t('groups.searchPlaceholder')}
          pageSize={20}
          getRowKey={(group) => group.id}
          columns={[
            { key: 'name', header: t('groups.name'), render: (group) => group.name, sortValue: (group) => group.name },
            { key: 'id', header: t('groups.groupId'), render: (group) => <code>{group.id}</code> },
            { key: 'members', header: t('groups.memberCount'), render: (group) => group.memberIds.length },
            {
              key: 'actions',
              header: t('common.actions'),
              render: (group) => (
                <button
                  type="button"
                  className={group.id === selectedGroupId ? undefined : 'secondary'}
                  onClick={() => {
                    if (group.id === selectedGroupId) { detailHeading.current?.focus(); return; }
                    setMemberLoading(true);
                    selectedGroupRef.current = group.id;
                    memberRequest.current += 1;
                    setMembers([]);
                    setMemberTotal(0);
                    setSelectedGroupId(group.id);
                    setMemberPage(0);
                    setMemberQuery('');
                    requestAnimationFrame(() => detailHeading.current?.focus());
                  }}
                >
                  {group.id === selectedGroupId ? t('groups.selected') : t('groups.selectGroup')}
                </button>
              )
            }
          ]}
        />
      </section>
      <section className="panel content-stack group-detail" aria-busy={memberLoading}>
        <div className="content-stack">
          <section className="form-grid">
            <h2 ref={detailHeading} tabIndex={-1}>{selectedGroup ? selectedGroup.name : t('groups.members')}</h2>
            {selectedGroup ? (
              <>
                <code>{selectedGroup.id}</code>
                <div className="muted">{t('groups.memberCountLabel', { count: selectedGroup.memberIds.length })}</div>
              </>
            ) : (
              <div className="muted">{t('groups.selectGroupPrompt')}</div>
            )}
          </section>
          <section className="form-grid">
            <h2>{t('groups.addMember')}</h2>
            <FormField label={t('groups.addMemberSearch')}
              placeholder={t('groups.addMemberSearch')}
              value={candidateQuery}
              onChange={(event) => {
                setCandidateQuery(event.target.value);
                setSelectedUserId('');
              }}
            />
            <label><span id="group-member-target">{t('groups.selectUser')}</span><select aria-labelledby="group-member-target" value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}>
              <option value="">{t('groups.selectUser')}</option>
              {candidateUsers.map((user) => {
                const blocking = blockingGroupFor(user.id);
                return (
                  <option key={user.id} value={user.id} disabled={blocking !== undefined}>
                    {blocking === undefined ? user.email : t('groups.candidateInGroup', { email: user.email, group: blocking })}
                  </option>
                );
              })}
            </select></label>
            <button
              disabled={memberLoading || !selectedGroupId || !selectedUserId || blockingGroupFor(selectedUserId) !== undefined}
              onClick={() => void updateMember('add')}
            >
              {t('groups.addMember')}
            </button>
          </section>
        </div>
        <DataTable
          key={selectedGroupId}
          rows={members}
          empty={memberLoading ? t('common.loading') : selectedGroup ? t('groups.noMembers') : t('groups.selectGroupPrompt')}
          mobileCards
          searchPlaceholder={t('groups.memberSearchPlaceholder')}
          pageSize={MEMBER_PAGE_SIZE}
          getRowKey={(user) => user.id}
          server={{
            total: memberTotal,
            page: memberPage,
            onPageChange: setMemberPage,
            onSearchChange: (value) => {
              setMemberQuery(value);
              setMemberPage(0);
            }
          }}
          columns={[
            { key: 'email', header: t('users.email'), render: (user) => user.email },
            { key: 'name', header: t('users.name'), render: (user) => user.name },
            {
              key: 'status',
              header: t('users.status'),
              render: (user) => <span className={`badge ${user.status}`}>{user.status}</span>
            },
            {
              key: 'actions',
              header: t('common.actions'),
              render: (user) => (
                <button className="secondary" onClick={() => void updateMember('remove', user.id)}>
                  {t('groups.removeMember')}
                </button>
              )
            }
          ]}
        />
      </section>
      </div>
      </div>
      </div>
    </section>
  );
}
